import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { Firestore, doc, getDoc, updateDoc, setDoc, collection, query, where, getDocs } from '@angular/fire/firestore';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from '@angular/fire/storage';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import {
  LpoTemplateData,
  LpoTemplateDialogComponent,
} from './lpo-template-dialog/lpo-template-dialog.component';
import {
  TimeSheetTemplateData,
  TimeSheetTemplateDialogComponent,
} from './time-sheet-template-dialog/time-sheet-template-dialog.component';
import { RoleSelectionDialogComponent } from '../../auth/role-selection-dialog/role-selection-dialog.component';
import { ImageCarouselDialogComponent } from './image-carousel-dialog/image-carousel-dialog.component';
import { LpoViewerDialogComponent } from 'src/app/components/lpo-viewer-dialog/lpo-viewer-dialog.component';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, updatePassword } from 'firebase/auth';
import { environment } from 'src/app/environments/environment';


@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit {
  @ViewChild('brandingEditorDialog') brandingEditorDialog!: TemplateRef<any>;
  activeEditorType: 'header' | 'footer' = 'header';

  openBrandingEditorDialog(type: 'header' | 'footer') {
    this.activeEditorType = type;
    this.dialog.open(this.brandingEditorDialog, {
      width: '95vw',
      maxWidth: '1200px',
      panelClass: 'branding-editor-dialog-container',
      disableClose: false
    });
  }

  currentUid: string | null = null;
  isLoading = true;
  isSaving = false;

  // Form Fields
  firstname: string = '';
  email: string = '';
  phoneNumber: string = '';
  countryCode: string = '+971'; // Default UAE
  telephone: string = '';
  telephoneCountryCode: string = '+971';

  countryList = [
    { name: 'UAE', code: '+971', flag: 'assets/flags/uae.png', limit: 9 },
    { name: 'India', code: '+91', flag: 'assets/flags/india.png', limit: 10 },
    {
      name: 'Saudi Arabia',
      code: '+966',
      flag: 'assets/flags/saudi.png',
      limit: 9,
    },
    { name: 'Kuwait', code: '+965', flag: 'assets/flags/kuwait.png', limit: 8 },
    { name: 'Oman', code: '+968', flag: 'assets/flags/oman.png', limit: 8 },
    { name: 'Qatar', code: '+974', flag: 'assets/flags/qatar.png', limit: 8 },
  ];

  get currentPhoneLimit(): number {
    const country = this.countryList.find((c) => c.code === this.countryCode);
    return country ? country.limit : 10; // Default to 10 if not found
  }

  getSelectedCountry() {
    return this.countryList.find((c) => c.code === this.countryCode);
  }

  getSelectedTelephoneCountry() {
    return this.countryList.find((c) => c.code === this.telephoneCountryCode);
  }

  // Allow only numbers
  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  companyName: string = '';
  companyDetails: string = '';
  companyAddress: string = '';
  tagline: string = '';
  website: string = '';
  fax: string = '';
  companyLogoUrl: string | null = null;
  companyLogoBase64: string | null = null; // Added
  companyDocuments: {
    name: string;
    url: string;
    path: string;
    savedAt?: any;
  }[] = [];

  get imageDocuments() {
    return this.companyDocuments.filter(doc => {
      const ext = doc.name.split('.').pop()?.toLowerCase();
      return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext || '');
    });
  }

  get pdfDocuments() {
    return this.companyDocuments.filter(doc => doc.name.toLowerCase().endsWith('.pdf'));
  }

  get docDocuments() {
    return this.companyDocuments.filter(doc => {
      const ext = doc.name.split('.').pop()?.toLowerCase();
      return ['doc', 'docx'].includes(ext || '');
    });
  }

  isUploadingDoc = false;
  // Quotation Branding
  quotationBranding: any = null;
  isUploadingLetterhead = false;
  /** True while polling Firestore for the Cloud Function's conversion result. */
  isProcessingLetterhead = false;
  isUploadingHeader = false;
  isUploadingFooter = false;

  // Canvas Editing Flags
  editingCanvasName = false;
  editingCanvasTagline = false;
  editingCanvasAddress = false;
  editingCanvasPhone = false;
  editingCanvasEmail = false;
  editingCanvasWebsite = false;
  editingCanvasFax = false;
  showLogoOptions = false;

  // Business License Fields
  businessLicenseNo: string = '';
    tradeLicenseVerified = false;
  trnNumber: string = '';
  licenseValidTill: Date | null = null;
  licenseIssuedBy: string = '';

  // LPO Template Fields
  lpoTemplates: any[] = [];
  selectedLpoIndex: number = -1; // -1 means none selected or creating new
  lpoTemplateHeader: string = '';
  lpoTemplateFooter: string = '';
  lpoTemplateTerms: string = '';
  lpoTemplateSignatureUrl: string = '';

  // Helper to select a template and populate fields
  selectLpoTemplate(index: number) {
    if (index >= 0 && index < this.lpoTemplates.length) {
      const t = this.lpoTemplates[index];
      this.selectedLpoIndex = index;
      this.lpoTemplateHeader = t.header || '';
      this.lpoTemplateFooter = t.footer || '';
      this.lpoTemplateTerms = t.terms || '';
      this.lpoTemplateSignatureUrl = t.signatureUrl || '';
    }
  }

  // Time Sheet Template Fields
  timeSheetTemplateHeader: string = '';
  timeSheetTemplateFooter: string = '';
  timeSheetTemplateTerms: string = '';
  timeSheetTemplateLogoUrl: string = '';
  timeSheetTemplateLogoBase64: string = '';
  timeSheetTemplateSignatureUrl: string = '';
  timeSheetTemplateSignatureBase64: string = '';

  role: 'customer' | 'business' | 'normaluser' | 'worker' | null = null;
  isIndividual: boolean = false;
  isVisiting: boolean = false;

  // ── Worker Management ────────────────────────────────────────────────
  workers: any[] = [];
  isLoadingWorkers = false;
  workerForm = { name: '', email: '', password: '' };
  showAddWorkerForm = false;
  isCreatingWorker = false;
  workerSettings = { enabled: true, defaultWorkerLimit: 2 };
  // ── END Worker Management ────────────────────────────────────────────

  selectedLogoFile: File | null = null;
  logoPreviewUrl: string | null = null;

  get backLink(): string {
    if (this.router.url.includes('/business')) {
      return '/business/Dashboard';
    }
    return '/';
  }

  get backLabel(): string {
    if (this.router.url.includes('/business')) {
      return 'Back to Dashboard';
    }
    return 'Back to Home';
  }

  constructor(
    private afs: Firestore,
    private auth: Auth,
    private ux: UxserviceService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private router: Router,
  ) { }

  ngOnInit(): void {
    onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        this.currentUid = user.uid;
        this.email = user.email || '';
        this.fetchUserProfile();
      } else {
        this.isLoading = false;
        // Handle not logged in if necessary, though route guard should handle it
      }
    });
  }

  // ── Get secondary Firebase app (for worker creation without logging out vendor) ──
  private getSecondaryAuth() {
    const secondaryAppName = 'workerCreation';
    let secondaryApp = getApps().find(a => a.name === secondaryAppName);
    if (!secondaryApp) {
      secondaryApp = initializeApp(environment.firebase, secondaryAppName);
    }
    return getAuth(secondaryApp);
  }

  async fetchUserProfile() {
    if (!this.currentUid) return;

    this.ux.showOverlay('Loading profile...');
    try {
      const userRef = doc(this.afs, 'users-list', this.currentUid);
      const snap = await getDoc(userRef);

      if (snap.exists()) {
        const data = snap.data();
        // Use firstname, fallback to displayname
        this.firstname = data['firstname'] || data['displayName'] || '';
        this.email = data['email'] || this.email;

        // Handle Phone Number split
        const fullPhone = data['phoneNumber'] || '';
        if (fullPhone) {
          const matchedCountry = this.countryList.find((c) =>
            fullPhone.startsWith(c.code),
          );
          if (matchedCountry) {
            this.countryCode = matchedCountry.code;
            this.phoneNumber = fullPhone.replace(matchedCountry.code, '');
          } else {
            // If no match found, keep default code and put everything in number, or handle as needed
            this.phoneNumber = fullPhone;
          }
        } else {
          this.phoneNumber = '';
        }

        // Handle Telephone split
        const fullTel = data['telephone'] || '';
        if (fullTel) {
          const matchedCountry = this.countryList.find((c) =>
            fullTel.startsWith(c.code),
          );
          if (matchedCountry) {
            this.telephoneCountryCode = matchedCountry.code;
            this.telephone = fullTel.replace(matchedCountry.code, '');
          } else {
            this.telephone = fullTel;
          }
        } else {
          this.telephone = '';
        }

        this.role = data['role'] || null;
        this.isIndividual = data['isIndividual'] || false;
        this.isVisiting = data['isVisiting'] || this.role === 'normaluser' || (this.role === 'customer' && this.isIndividual) || false;

        // 🚫 Workers cannot access the profile page
        if (this.role === 'worker') {
          this.ux.instantCloseOverlay();
          this.ux.showError('Workers do not have access to the profile page.');
          this.router.navigate(['/business/Dashboard']);
          return;
        }

        // Load workers list if vendor
        if (this.role === 'business') {
          this.loadWorkers();
        }

        this.companyName = data['companyName'] || data['accountdetails']?.['companyname'] || '';
        this.companyDetails = data['companyDetails'] || '';
        this.companyAddress = data['companyAddress'] || data['accountdetails']?.['companyAddress'] || '';
        this.tagline = data['tagline'] || data['companyTagline'] || '';
        this.website = data['website'] || data['companyWebsite'] || '';
        this.fax = data['fax'] || data['faxNumber'] || '';
        this.companyLogoUrl = data['companyLogoUrl'] || null;
        this.companyLogoBase64 = data['companyLogoBase64'] || null;
        this.companyDocuments = data['companyDocuments'] || data['accountdetails']?.['companyDocuments'] || [];

        const branding = data['quotationBranding'] || {};
        if (branding.headerLogoLeft === undefined) branding.headerLogoLeft = 6;
        if (branding.headerLogoTop === undefined) branding.headerLogoTop = 15;
        if (branding.headerLogoWidth === undefined) branding.headerLogoWidth = 10;
        if (branding.headerLogoHeight === undefined) branding.headerLogoHeight = 70;
        if (branding.headerNameLeft === undefined) branding.headerNameLeft = 18;
        if (branding.headerNameTop === undefined) branding.headerNameTop = 38;
        if (branding.headerTaglineLeft === undefined) branding.headerTaglineLeft = 18;
        if (branding.headerTaglineTop === undefined) branding.headerTaglineTop = 58;
        if (branding.headerContactLeft === undefined || branding.headerContactLeft !== 68) branding.headerContactLeft = 68;
        if (branding.headerContactTop === undefined) branding.headerContactTop = 15;
        if (branding.headerContactWidth === undefined || branding.headerContactWidth !== 30) branding.headerContactWidth = 30;
        if (branding.footerContactLeft === undefined) branding.footerContactLeft = 50;
        if (branding.footerContactTop === undefined) branding.footerContactTop = 40;
        if (branding.themeColor === undefined) branding.themeColor = '#ffffff';
        this.quotationBranding = branding;

        // Legacy Support: The previous code logic saved the local UI 'blob:' instead of the Firebase URL.
        const fallbackUrl = data['tradeLicenseCopyUrl'] || data['accountdetails']?.['tradeLicenseCopyUrl'] || data['accountdetails']?.['traceLicenseCopyUrl'];
        if (this.companyDocuments.length > 0 && fallbackUrl) {
          this.companyDocuments.forEach(doc => {
            if (!doc.url || doc.url.includes('blob:')) {
              doc.url = fallbackUrl;
            }
          });
        }

        // Business License
        this.businessLicenseNo = data['businessLicenseNo'] || data['accountdetails']?.['tradeLicenseNo'] || '';
                this.tradeLicenseVerified = Boolean(
                  data['tradeLicenseVerified'] ?? data['accountdetails']?.['tradeLicenseVerified']
                );
        this.trnNumber = data['trnNumber'] || '';
        this.licenseIssuedBy = data['licenseIssuedBy'] || data['accountdetails']?.['issuedBy'] || '';

        // Parse Timestamp for licenseValidTill
        if (data['licenseValidTill']) {
          if (data['licenseValidTill'].toDate) {
            this.licenseValidTill = data['licenseValidTill'].toDate();
          } else if (data['licenseValidTill'] instanceof Date) {
            this.licenseValidTill = data['licenseValidTill'];
          } else if (typeof data['licenseValidTill'] === 'string') {
            this.licenseValidTill = new Date(data['licenseValidTill']);
          }
        }

        // --- LPO Template Migration & Init ---
        // New Array Field
        this.lpoTemplates = data['lpoTemplates'] || [];

        // Migration: If no array but old object exists, move it to array
        const oldLpoTemplate = data['lpoTemplate'];
        if (
          this.lpoTemplates.length === 0 &&
          oldLpoTemplate &&
          (oldLpoTemplate.header || oldLpoTemplate.terms)
        ) {
          // Create a name for the migrated template
          const migratedTemplate = {
            ...oldLpoTemplate,
            name: 'Default Template',
          };
          this.lpoTemplates.push(migratedTemplate);
        }

        // Select first template by default if available
        if (this.lpoTemplates.length > 0) {
          this.selectLpoTemplate(0);
        } else if (this.role === 'customer' && !this.isIndividual && this.companyName) {
          // Automatically generate if company info exists but no template
          this.generateDefaultLpoTemplate();
        } else {
          // Reset fields
          this.lpoTemplateHeader = '';
          this.lpoTemplateFooter = '';
          this.lpoTemplateTerms = '';
          this.lpoTemplateSignatureUrl = '';
          this.selectedLpoIndex = -1;
        }

        // Time Sheet Template
        const timeSheetTemplate = data['timeSheetTemplate'] || {};
        this.timeSheetTemplateHeader = timeSheetTemplate.header || '';
        this.timeSheetTemplateFooter = timeSheetTemplate.footer || '';
        this.timeSheetTemplateTerms = timeSheetTemplate.terms || '';
        this.timeSheetTemplateLogoUrl = timeSheetTemplate.logoUrl || '';
        this.timeSheetTemplateLogoBase64 = timeSheetTemplate.logoBase64 || '';
        this.timeSheetTemplateSignatureUrl = timeSheetTemplate.signatureUrl || '';
        this.timeSheetTemplateSignatureBase64 = timeSheetTemplate.signatureBase64 || '';

        if (this.companyLogoUrl) {
          this.logoPreviewUrl = this.companyLogoUrl;
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      this.ux.showError('Failed to load profile.');
    } finally {
      this.isLoading = false;
      this.ux.instantCloseOverlay();
    }
  }

  async onLogoSelected(event: any) {
    const file = event.target.files[0];
    if (file && this.currentUid) {
      try {
        this.ux.showOverlay('Optimizing & Uploading Logo...');

        // 1. Resize/Compress Image for faster upload and PDF handling
        // A logo doesn't need to be huge. 400px is plenty for documents.
        const optimizedBlob = await this.resizeImage(file, 400, 400);
        const optimizedFile = new File([optimizedBlob], file.name, { type: 'image/jpeg' });

        // 2. Convert to Base64 (Small size now!)
        const base64 = await this.fileToBase64(optimizedFile);

        // 3. Upload to Firebase Storage (Much faster now)
        const storage = getStorage();
        const path = `company_logos/${this.currentUid}/${Date.now()}_logo.jpg`;
        const storageRef = ref(storage, path);
        const uploadResult = await uploadBytes(storageRef, optimizedFile);
        const url = await getDownloadURL(uploadResult.ref);

        // Instant Save to Firestore
        const userRef = doc(this.afs, 'users-list', this.currentUid);
        await updateDoc(userRef, {
          companyLogoUrl: url,
          companyLogoBase64: base64,
          photoURL: url,
        });

        // Update Local State
        this.companyLogoUrl = url;
        this.logoPreviewUrl = url;
        this.ux.showSuccess('Logo updated successfully!');
      } catch (error) {
        console.error('Error uploading logo:', error);
        this.ux.showError('Failed to upload logo. Please try a smaller image.');
      } finally {
        this.ux.instantCloseOverlay();
      }
    }
  }

  private resizeImage(file: File, maxWidth: number, maxHeight: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event: any) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas to Blob failed'));
          }, 'image/jpeg', 0.8); // 80% quality JPEG
        };
      };
      reader.onerror = (error) => reject(error);
    });
  }

  private fileToBase64(file: File | Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  async deleteLogo() {
    if (!this.currentUid || !this.companyLogoUrl) return;

    if (
      !(await this.ux.showConfirmation(
        'Are you sure you want to delete the company logo?',
        'Delete Logo',
      ))
    )
      return;

    this.ux.showOverlay('Deleting logo...');
    this.isSaving = true;
    try {
      // Optional: Delete from storage if you want to clean up
      // For now, we just remove the URL reference from Firestore

      const userRef = doc(this.afs, 'users-list', this.currentUid!);
      await updateDoc(userRef, {
        companyLogoUrl: null,
        companyLogoBase64: null, // Also clear base64
        photoURL: null, // Clear navbar avatar too
      });

      this.companyLogoUrl = null;
      this.logoPreviewUrl = null;
      this.selectedLogoFile = null;
      this.ux.showSuccess('Logo removed successfully');
    } catch (error) {
      console.error('Error deleting logo:', error);
      this.ux.showError('Failed to delete logo');
    } finally {
      this.isSaving = false;
      this.ux.instantCloseOverlay();
    }
  }

  saveProfile() {
    if (!this.currentUid) return;

    this.isSaving = true;

    // ... existingsaveProfile logic needs slight adjustment to not overwrite if just saving other details
    // For simplicity, we keep saveProfile as is, but deleteLogo handles its own saving.
    // However, if we saveProfile AFTER deleting logo, we need to ensure companyLogoUrl is null.

    // We'll just continue with existing saveProfile logic as it uses this.companyLogoUrl

    this.processSaveProfile();
  }

  async processSaveProfile() {
    this.ux.showOverlay('Saving profile changes...');
    try {
      let newLogoUrl = this.companyLogoUrl;

      const updateData: any = {
        firstname: this.firstname,
        displayName: this.firstname,
        phoneNumber: `${this.countryCode}${this.phoneNumber}`,
        telephone: `${this.telephoneCountryCode}${this.telephone}`,
      };

      if (!this.isIndividual && !this.isVisiting) {
        updateData.companyName = this.companyName;
        updateData.companyAddress = this.companyAddress;
        updateData.tagline = this.tagline;
        updateData.website = this.website;
        updateData.fax = this.fax;
        updateData.companyLogoUrl = newLogoUrl;
        updateData.photoURL = newLogoUrl;
        updateData.businessLicenseNo = this.businessLicenseNo;
        updateData.trnNumber = this.trnNumber;
        updateData.licenseValidTill = this.licenseValidTill;
        updateData.licenseIssuedBy = this.licenseIssuedBy;
      }

      if (this.role === 'business' && this.quotationBranding) {
        updateData.quotationBranding = this.quotationBranding;
        const accRef = doc(this.afs, 'accountdetails', this.currentUid!);
        await setDoc(accRef, { quotationBranding: this.quotationBranding }, { merge: true });
      }

      // Allow update of role-specific template data if needed
      if (this.role === 'customer') {
        // Auto-generate if missing before saving
        if (this.lpoTemplates.length === 0 && !this.isIndividual && this.companyName) {
          this.lpoTemplates.push({
            name: 'Default Template',
            header: 'TRN: ' + (this.businessLicenseNo || 'XXXXXXXXXXXXXXX'),
            terms: '1. Payment within 30 days.\n2. Equipment must be insured.\n3. Operator must follow safety guidelines.',
            footer: 'Authorized Signatory',
            signatureUrl: ''
          });
          this.selectedLpoIndex = 0;
        }

        // Verify if we need to update the currently selected template in the array before saving
        if (
          this.selectedLpoIndex > -1 &&
          this.selectedLpoIndex < this.lpoTemplates.length
        ) {
          this.lpoTemplates[this.selectedLpoIndex] = {
            name:
              this.lpoTemplates[this.selectedLpoIndex].name ||
              'Template ' + (this.selectedLpoIndex + 1),
            header: this.lpoTemplateHeader,
            footer: this.lpoTemplateFooter,
            terms: this.lpoTemplateTerms,
            signatureUrl: this.lpoTemplateSignatureUrl,
          };
        }
        updateData.lpoTemplates = this.lpoTemplates;
        // Optionally keep the single field for backward compatibility or easy access to "active" one,
        // but user asked for first one to be taken. We can save 'lpoTemplate' as the first one for legacy systems.
        if (this.lpoTemplates.length > 0) {
          updateData.lpoTemplate = this.lpoTemplates[0];
        }
      } else if (this.role === 'business') {
        updateData.timeSheetTemplate = {
          header: this.timeSheetTemplateHeader,
          footer: this.timeSheetTemplateFooter,
          terms: this.timeSheetTemplateTerms,
          logoUrl: this.timeSheetTemplateLogoUrl,
          logoBase64: this.timeSheetTemplateLogoBase64,
          signatureUrl: this.timeSheetTemplateSignatureUrl,
          signatureBase64: this.timeSheetTemplateSignatureBase64,
        };
      }

      const userRef = doc(this.afs, 'users-list', this.currentUid!);
      await updateDoc(userRef, updateData);

      this.ux.showSuccess('Profile updated successfully!');
      this.companyLogoUrl = newLogoUrl; // Update local state
      this.selectedLogoFile = null;
    } catch (error) {
      console.error('Error updating profile:', error);
      this.ux.showError('Failed to update profile.');
    } finally {
      this.isSaving = false;
      this.ux.instantCloseOverlay();
    }
  }

  triggerFileInput() {
    document.getElementById('logoInput')?.click();
  }

  openLpoTemplateDialog(index: number = -1) {
    // If index is -1, we are adding a new one. clear fields before opening?
    // Or just pass empty/current fields.

    let initialData: LpoTemplateData;

    if (index > -1 && index < this.lpoTemplates.length) {
      // EDIT MODE
      const t = this.lpoTemplates[index];
      initialData = {
        companyName: this.companyName,
        companyDetails: this.companyAddress,
        phoneNumber: this.phoneNumber,
        telephoneNumber: t.telephoneNumber || '', // Pass existing or empty
        email: this.email,
        lpoHeader: t.header,
        lpoTerms: t.terms,
        lpoFooter: t.footer,
        logoUrl: this.companyLogoUrl,
        logoBase64: this.companyLogoBase64,
        signatureUrl: t.signatureUrl,
        signatureBase64: t.signatureBase64, // Pass base64
      } as LpoTemplateData;
    } else {
      // ADD MODE - Defaults
      initialData = {
        companyName: this.companyName,
        companyDetails: this.companyAddress,
        phoneNumber: this.phoneNumber,
        telephoneNumber: this.telephone, // Default to profile telephone
        email: this.email,
        lpoHeader: '',
        lpoTerms: '',
        lpoFooter: '',
        logoUrl: this.companyLogoUrl,
        logoBase64: this.companyLogoBase64,
        signatureUrl: '',
        signatureBase64: '',
      } as LpoTemplateData;
    }

    const dialogRef = this.dialog.open(LpoTemplateDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: initialData,
      panelClass: ['lpo-dialog', 'no-padding-dialog'],
    });

    dialogRef.afterClosed().subscribe((result: LpoTemplateData) => {
      if (result) {
        // We update common company info regardless of template
        this.companyName = result.companyName;
        this.companyAddress = result.companyDetails;
        this.phoneNumber = result.phoneNumber;
        this.telephone = result.telephoneNumber; // Update profile telephone

        // Update Logo if changed in dialog
        if (result.logoUrl) {
          this.companyLogoUrl = result.logoUrl;
        }

        const newTemplateObj = {
          name:
            'Template ' +
            (index > -1 ? index + 1 : this.lpoTemplates.length + 1), // Simple naming for now
          header: result.lpoHeader,
          terms: result.lpoTerms,
          footer: result.lpoFooter,
          signatureUrl: result.signatureUrl || '',
          signatureBase64: result.signatureBase64 || '', // Store base64
          telephoneNumber: result.telephoneNumber, // Added to template storage
          phoneNumber: result.phoneNumber, // Store specific phone too
        };

        if (index > -1 && index < this.lpoTemplates.length) {
          // Updated Existing
          this.lpoTemplates[index] = {
            ...this.lpoTemplates[index],
            ...newTemplateObj,
          };
          this.selectLpoTemplate(index); // Ensure fields match
        } else {
          // Added New
          this.lpoTemplates.push(newTemplateObj);
          this.selectLpoTemplate(this.lpoTemplates.length - 1); // Select the new one
        }

        this.saveProfile();
      }
    });
  }

  previewSampleLpo(index: number) {
    if (index < 0 || index >= this.lpoTemplates.length) return;

    const t = this.lpoTemplates[index];
    const mockBooking = {
      id: 'SAMPLE-123',
      rate: 1500,
      totalAmount: 4500,
      priceType: 'daily',
      equipment: {
        name: 'Sample Equipment 30T',
        brand: 'Caterpillar',
        model: 'CAT-2024',
        plate_number: 'DXB-12345'
      },
      customer: {
        startDate: { toDate: () => new Date() },
        endDate: { toDate: () => new Date(Date.now() + 2 * 24 * 60 * 60 * 1000) },
        location: 'Business Bay, Dubai, UAE',
        priceType: 'daily'
      }
    };

    const sampleCustomer = {
      companyName: this.companyName || 'Your Company Name',
      companyDetails: this.companyAddress || 'Your Address / Company Details',
      phoneNumber: this.phoneNumber || '050 123 4567',
      telephoneNumber: t.telephoneNumber || this.telephone, // Include landline
      email: this.email || 'company@example.com',
      logoUrl: this.companyLogoUrl,
      logoBase64: this.companyLogoBase64, // Added
      lpoHeader: t.header,
      lpoTerms: t.terms,
      lpoFooter: t.footer,
      signatureUrl: t.signatureUrl,
      signatureBase64: t.signatureBase64 // Added signature base64
    };

    const sampleVendor = {
      companyname: 'Aluguloo Heavy Equipments LLC',
      address: 'Industrial Area, Al Qouz, Dubai',
      phoneNumber: '+971 4 123 4567',
      emailid: 'vendor@example.com'
    };

    this.dialog.open(LpoViewerDialogComponent, {
      width: '1000px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        booking: mockBooking,
        isSample: true,
        sampleData: {
          customerDetails: sampleCustomer,
          vendorDetails: sampleVendor
        }
      },
      panelClass: 'lpo-viewer-panel',
      disableClose: true,
    });
  }

  generateDefaultLpoTemplate() {
    const defaultTemplate = {
      name: 'Default Template',
      header: 'TRN: 123456789012345',
      terms: '1. Payment within 30 days.\n2. Equipment must be insured.\n3. Operator must follow safety guidelines.',
      footer: 'Authorized Signatory',
      signatureUrl: ''
    };

    this.lpoTemplates.push(defaultTemplate);
    this.selectLpoTemplate(this.lpoTemplates.length - 1);
    this.ux.showSuccess('Default template generated from your profile!');
    this.saveProfile();
  }

  async deleteLpoTemplate(index: number) {
    if (index < 0 || index >= this.lpoTemplates.length) return;

    if (
      !(await this.ux.showConfirmation(
        'Are you sure you want to delete this template?',
        'Delete Template',
      ))
    )
      return;

    this.lpoTemplates.splice(index, 1);

    // Adjust selection if needed
    if (this.selectedLpoIndex === index) {
      this.selectedLpoIndex = -1;
      this.lpoTemplateHeader = '';
      this.lpoTemplateFooter = '';
      this.lpoTemplateTerms = '';
      this.lpoTemplateSignatureUrl = '';
    } else if (this.selectedLpoIndex > index) {
      this.selectedLpoIndex--;
    }

    this.saveProfile();
  }

  openTimeSheetTemplateDialog() {
    const dialogRef = this.dialog.open(TimeSheetTemplateDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        companyName: this.companyName,
        companyDetails: this.companyAddress,
        phoneNumber: this.phoneNumber,
        header: this.timeSheetTemplateHeader,
        terms: this.timeSheetTemplateTerms,
        footer: this.timeSheetTemplateFooter,
        logoUrl: this.timeSheetTemplateLogoUrl || this.companyLogoUrl, // Use TS-specific or profile logo
        logoBase64: this.timeSheetTemplateLogoBase64 || this.companyLogoBase64,
        signatureUrl: this.timeSheetTemplateSignatureUrl,
        signatureBase64: this.timeSheetTemplateSignatureBase64,
      } as TimeSheetTemplateData,
      panelClass: ['lpo-dialog', 'no-padding-dialog'],
    });

    dialogRef.afterClosed().subscribe((result: TimeSheetTemplateData) => {
      if (result) {
        this.companyName = result.companyName;
        this.companyAddress = result.companyDetails;
        this.phoneNumber = result.phoneNumber;
        this.timeSheetTemplateHeader = result.header;
        this.timeSheetTemplateTerms = result.terms;
        this.timeSheetTemplateFooter = result.footer;
        this.timeSheetTemplateLogoUrl = result.logoUrl || '';
        this.timeSheetTemplateLogoBase64 = result.logoBase64 || '';
        this.timeSheetTemplateSignatureUrl = result.signatureUrl || '';
        this.timeSheetTemplateSignatureBase64 = result.signatureBase64 || '';

        this.saveProfile();
      }
    });
  }

  getProfileImageStyle(): SafeStyle {
    const url = this.logoPreviewUrl || '/assets/landingpage/Avatar.png';
    return this.sanitizer.bypassSecurityTrustStyle(`url('${url}')`);
  }

  // --- Document Upload Logic ---

  triggerDocUpload() {
    document.getElementById('docInput')?.click();
  }

  async onDocSelected(event: any) {
    const file = event.target.files[0];
    if (!file || !this.currentUid) return;

    this.isUploadingDoc = true;
    try {
      const storage = getStorage();
      const filePath = `company_documents/${this.currentUid}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filePath);

      const uploadResult = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(uploadResult.ref);

      const newDoc = {
        name: file.name,
        url: downloadUrl,
        path: filePath,
        savedAt: new Date(),
      };

      // Update Local State
      this.companyDocuments.push(newDoc);

      // Save to Firestore immediately to persist the upload
      const userRef = doc(this.afs, 'users-list', this.currentUid);
      await updateDoc(userRef, {
        companyDocuments: this.companyDocuments,
      });

    } catch (error) {
      console.error('Error uploading document:', error);
      this.ux.showError('Failed to upload document');
    } finally {
      this.isUploadingDoc = false;
    }
  }

  async deleteDocument(docItem: any, index: number) {
    if (
      !(await this.ux.showConfirmation(
        'Are you sure you want to delete this document?',
        'Delete Document',
      ))
    )
      return;

    try {
      // 1. Delete from Storage (if path exists)
      if (docItem.path) {
        const storage = getStorage();
        const storageRef = ref(storage, docItem.path);
        await deleteObject(storageRef).catch((e) =>
          console.warn('Storage delete failed or file missing', e),
        );
      }

      // 2. Remove from list
      this.companyDocuments.splice(index, 1);

      // 3. Update Firestore
      if (this.currentUid) {
        const userRef = doc(this.afs, 'users-list', this.currentUid);
        await updateDoc(userRef, {
          companyDocuments: this.companyDocuments,
        });
      }

      this.ux.showSuccess('Document deleted successfully');
    } catch (error) {
      console.error('Error deleting document:', error);
      this.ux.showError('Failed to delete document');
    }
  }

  viewDocument(docItem: any) {
    const ext = docItem.name.split('.').pop()?.toLowerCase() || '';
    if (ext === 'pdf' || ext === 'doc' || ext === 'docx') {
      window.open(docItem.url, '_blank');
    } else {
      // It's an image, open carousel
      const images = this.imageDocuments;
      const initialIndex = images.findIndex(img => img.url === docItem.url);

      this.dialog.open(ImageCarouselDialogComponent, {
        width: '90vw',
        maxWidth: '1200px',
        panelClass: 'carousel-dialog-container',
        data: { images, initialIndex: initialIndex > -1 ? initialIndex : 0 }
      });
    }
  }
  triggerLetterheadUpload() {
    document.getElementById('letterheadInput')?.click();
  }

  async onLetterheadSelected(event: any) {
    const file = event.target.files[0];
    if (!file || !this.currentUid) return;
    if (file.type !== 'application/pdf') {
      this.ux.showError('Only PDF files are allowed for letterhead.');
      return;
    }

    this.isUploadingLetterhead = true;
    this.ux.showOverlay('Uploading Letterhead...');
    try {
      const storage = getStorage();
      // Delete old if exists
      if (this.quotationBranding?.storagePath) {
        const oldRef = ref(storage, this.quotationBranding.storagePath);
        await deleteObject(oldRef).catch(() => { });
      }

      const filePath = `quotation_letterheads/${this.currentUid}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filePath);
      const result = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(result.ref);

      this.quotationBranding = {
        letterheadUrl: url,        // keep for backward compat display
        pdfUrl: url,
        pdfStoragePath: filePath,
        storagePath: filePath,     // keep old field so existing code doesn't break
        uploadedAt: new Date(),
        fileName: file.name,
        imageUrl: null,            // will be populated by Cloud Function
        imageStoragePath: null,
      };

      const userRef = doc(this.afs, 'users-list', this.currentUid);
      await updateDoc(userRef, { quotationBranding: this.quotationBranding });
      this.ux.showSuccess('Letterhead uploaded! Converting to image, this takes a few seconds...');

      this.isUploadingLetterhead = false;
      this.ux.instantCloseOverlay();

      // The local quotationBranding object above is provisional (imageUrl /
      // letterheadBase64 are still null). Don't let it sit there as if it
      // were final — poll Firestore until the Cloud Function finishes and
      // replace it wholesale with the authoritative server value.
      await this.pollForLetterheadConversion();
      return;
    } catch (e) {
      this.ux.showError('Upload failed.');
    } finally {
      this.isUploadingLetterhead = false;
      this.ux.instantCloseOverlay();
    }
  }

  /**
   * Polls Firestore every 1s (up to 15s) waiting for the Cloud Function
   * (convertLetterhead) to write quotationBranding.imageUrl and/or
   * quotationBranding.letterheadBase64. As soon as either field appears,
   * the entire quotationBranding object is reloaded from Firestore and
   * replaces the local one — never merged with stale local data, so every
   * field the Cloud Function wrote (imageUrl, imageStoragePath,
   * letterheadBase64, plus the pre-existing pdfUrl/pdfStoragePath/
   * storagePath/letterheadUrl) survives intact.
   */
  private async pollForLetterheadConversion(): Promise<void> {
    if (!this.currentUid) return;

    this.isProcessingLetterhead = true;
    const userRef = doc(this.afs, 'users-list', this.currentUid);
    const maxAttempts = 15;
    const intervalMs = 1000;

    try {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));

        const snap = await getDoc(userRef);
        if (!snap.exists()) continue;

        const data = snap.data();
        const branding = data['quotationBranding'];

        if (branding && (branding.imageUrl || branding.letterheadBase64)) {
          // Conversion finished server-side — adopt the Firestore value as-is.
          this.quotationBranding = branding;
          this.ux.showSuccess('Letterhead ready — your quotations will now use it.');
          return;
        }
      }

      // Exhausted all attempts — conversion may still be running in the
      // background (Cloud Function cold start, large PDF, etc).
      this.ux.showError(
        'Letterhead uploaded but conversion is still processing. Please refresh in a few seconds.',
      );
    } finally {
      this.isProcessingLetterhead = false;
    }
  }

  async deleteLetterhead() {
    if (!this.quotationBranding || !this.currentUid) return;
    if (!(await this.ux.showConfirmation('Delete letterhead?', 'Delete'))) return;

    this.ux.showOverlay('Deleting...');
    try {
      const storage = getStorage();
      if (this.quotationBranding.storagePath) {
        const storageRef = ref(storage, this.quotationBranding.storagePath);
        await deleteObject(storageRef).catch(() => { });
      }
      const userRef = doc(this.afs, 'users-list', this.currentUid);
      await updateDoc(userRef, { quotationBranding: null });
      this.quotationBranding = null;
      this.ux.showSuccess('Letterhead deleted.');
    } catch (e) {
      this.ux.showError('Delete failed.');
    } finally {
      this.ux.instantCloseOverlay();
    }
  }
  openSwitchRoleDialog() {
    const dialogRef = this.dialog.open(RoleSelectionDialogComponent, {
      disableClose: true,
      panelClass: 'role-selection-panel',
      width: '560px',
      maxWidth: '95vw',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result !== 'skip') {
        // Role was changed — reload profile to pick up new role
        this.fetchUserProfile();
      }
    });
  }

  triggerHeaderUpload() {
    document.getElementById('headerInput')?.click();
  }

  triggerFooterUpload() {
    document.getElementById('footerInput')?.click();
  }

  async onHeaderSelected(event: any) {
    const file = event.target.files[0];
    if (!file || !this.currentUid) return;

    this.isUploadingHeader = true;
    this.ux.showOverlay('Uploading Header Image...');
    try {
      const base64 = await this.fileToBase64(file);
      const storage = getStorage();
      const filePath = `brandings/${this.currentUid}/quotation_header_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filePath);
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(uploadResult.ref);

      if (!this.quotationBranding) {
        this.quotationBranding = { documentType: 'quotation' };
      }
      this.quotationBranding.headerImageUrl = downloadUrl;
      this.quotationBranding.headerBase64 = base64;

      const userRef = doc(this.afs, 'users-list', this.currentUid);
      await updateDoc(userRef, { quotationBranding: this.quotationBranding });

      const accRef = doc(this.afs, 'accountdetails', this.currentUid);
      await setDoc(accRef, { quotationBranding: this.quotationBranding }, { merge: true });

      this.ux.showSuccess('Header uploaded successfully!');
    } catch (error) {
      console.error('Error uploading header:', error);
      this.ux.showError('Failed to upload header');
    } finally {
      this.isUploadingHeader = false;
      this.ux.instantCloseOverlay();
    }
  }

  async onFooterSelected(event: any) {
    const file = event.target.files[0];
    if (!file || !this.currentUid) return;

    this.isUploadingFooter = true;
    this.ux.showOverlay('Uploading Footer Image...');
    try {
      const base64 = await this.fileToBase64(file);
      const storage = getStorage();
      const filePath = `brandings/${this.currentUid}/quotation_footer_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filePath);
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(uploadResult.ref);

      if (!this.quotationBranding) {
        this.quotationBranding = { documentType: 'quotation' };
      }
      this.quotationBranding.footerImageUrl = downloadUrl;
      this.quotationBranding.footerBase64 = base64;

      const userRef = doc(this.afs, 'users-list', this.currentUid);
      await updateDoc(userRef, { quotationBranding: this.quotationBranding });

      const accRef = doc(this.afs, 'accountdetails', this.currentUid);
      await setDoc(accRef, { quotationBranding: this.quotationBranding }, { merge: true });

      this.ux.showSuccess('Footer uploaded successfully!');
    } catch (error) {
      console.error('Error uploading footer:', error);
      this.ux.showError('Failed to upload footer');
    } finally {
      this.isUploadingFooter = false;
      this.ux.instantCloseOverlay();
    }
  }

  async deleteHeader() {
    if (!this.currentUid || !this.quotationBranding) return;
    if (!(await this.ux.showConfirmation('Are you sure you want to delete the quotation header?', 'Delete Header'))) return;

    this.ux.showOverlay('Deleting Header...');
    try {
      this.quotationBranding.headerImageUrl = null;
      this.quotationBranding.headerBase64 = null;

      const userRef = doc(this.afs, 'users-list', this.currentUid);
      await updateDoc(userRef, { quotationBranding: this.quotationBranding });

      const accRef = doc(this.afs, 'accountdetails', this.currentUid);
      await setDoc(accRef, { quotationBranding: this.quotationBranding }, { merge: true });

      this.ux.showSuccess('Header removed successfully');
    } catch (error) {
      console.error('Error deleting header:', error);
      this.ux.showError('Failed to delete header');
    } finally {
      this.ux.instantCloseOverlay();
    }
  }

  async deleteFooter() {
    if (!this.currentUid || !this.quotationBranding) return;
    if (!(await this.ux.showConfirmation('Are you sure you want to delete the quotation footer?', 'Delete Footer'))) return;

    this.ux.showOverlay('Deleting Footer...');
    try {
      this.quotationBranding.footerImageUrl = null;
      this.quotationBranding.footerBase64 = null;

      const userRef = doc(this.afs, 'users-list', this.currentUid);
      await updateDoc(userRef, { quotationBranding: this.quotationBranding });

      const accRef = doc(this.afs, 'accountdetails', this.currentUid);
      await setDoc(accRef, { quotationBranding: this.quotationBranding }, { merge: true });

      this.ux.showSuccess('Footer removed successfully');
    } catch (error) {
      console.error('Error deleting footer:', error);
      this.ux.showError('Failed to delete footer');
    } finally {
      this.ux.instantCloseOverlay();
    }
  }

  // ──────────────────────────────────────────────────────────────────────────────
  //  WORKER MANAGEMENT METHODS
  // ──────────────────────────────────────────────────────────────────────────────

  /** Load all workers belonging to this vendor */
  async loadWorkers() {
    if (!this.currentUid) return;
    this.isLoadingWorkers = true;
    try {
      // Fetch system settings
      const settingsSnap = await getDoc(doc(this.afs, 'system_settings', 'worker_management'));
      if (settingsSnap.exists()) {
        this.workerSettings = settingsSnap.data() as { enabled: boolean; defaultWorkerLimit: number };
      }

      const q = query(
        collection(this.afs, 'users-list'),
        where('role', '==', 'worker'),
        where('parentUid', '==', this.currentUid),
      );
      const snap = await getDocs(q);
      this.workers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.error('Error loading workers', e);
    } finally {
      this.isLoadingWorkers = false;
    }
  }

  /** Show/hide the add-worker form */
  toggleAddWorkerForm() {
    this.showAddWorkerForm = !this.showAddWorkerForm;
    if (!this.showAddWorkerForm) {
      this.workerForm = { name: '', email: '', password: '' };
    }
  }

  /** Create a new worker Firebase account + Firestore record */
  async createWorker() {
    const { name, email, password } = this.workerForm;

    if (!this.workerSettings.enabled) {
      this.ux.showError('Worker Management is disabled globally by the Super Admin.');
      return;
    }

    if (this.workers.length >= this.workerSettings.defaultWorkerLimit) {
      this.ux.showError(`Cannot create worker. Limit of ${this.workerSettings.defaultWorkerLimit} workers has been reached.`);
      return;
    }

    if (!name || !email || !password) {
      this.ux.showError('Please fill in all fields (Name, Email, Password).');
      return;
    }

    if (password.length < 6) {
      this.ux.showError('Password must be at least 6 characters.');
      return;
    }

    if (!this.currentUid) return;

    this.isCreatingWorker = true;
    this.ux.showOverlay('Creating worker account...');

    try {
      // Use secondary app so vendor does NOT get signed out
      const secondaryAuth = this.getSecondaryAuth();
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      const workerUid = cred.user.uid;

      // Sign out from secondary app immediately
      await secondaryAuth.signOut();

      // Create worker Firestore record
      const workerRef = doc(this.afs, 'users-list', workerUid);
      await setDoc(workerRef, {
        uid: workerUid,
        firstname: name,
        displayName: name,
        email: email.toLowerCase(),
        role: 'worker',
        parentUid: this.currentUid,
        passwordText: password,          // stored for vendor reference
        isdeleted: false,
        createdAt: new Date().toISOString(),
        lastLoginAt: null,
      });

      this.ux.showSuccess(`Worker "${name}" created successfully!`);
      this.workerForm = { name: '', email: '', password: '' };
      this.showAddWorkerForm = false;
      await this.loadWorkers();           // refresh list
    } catch (error: any) {
      console.error('Error creating worker:', error);
      this.ux.showError('Failed to create worker: ' + error.message);
    } finally {
      this.isCreatingWorker = false;
      this.ux.instantCloseOverlay();
    }
  }

  /** Change a worker's password */
  async changeWorkerPassword(worker: any) {
    const newPassword = prompt(`Enter new password for ${worker.firstname || worker.email}:`);
    if (!newPassword) return;

    if (newPassword.length < 6) {
      this.ux.showError('Password must be at least 6 characters.');
      return;
    }

    this.ux.showOverlay('Updating password...');
    try {
      // Use secondary app to sign in as worker and update their password
      const secondaryAuth = this.getSecondaryAuth();
      const { signInWithEmailAndPassword: signIn } = await import('firebase/auth');
      const cred = await signIn(secondaryAuth, worker.email, worker.passwordText);
      await updatePassword(cred.user, newPassword);
      await secondaryAuth.signOut();

      // Update passwordText in Firestore
      const workerRef = doc(this.afs, 'users-list', worker.id);
      await updateDoc(workerRef, { passwordText: newPassword });

      // Update local state
      worker.passwordText = newPassword;

      this.ux.showSuccess('Password updated successfully!');
    } catch (error: any) {
      console.error('Error changing worker password:', error);
      this.ux.showError('Failed to update password: ' + error.message);
    } finally {
      this.ux.instantCloseOverlay();
    }
  }

  /** Soft-delete a worker (they can no longer log in) */
  async deleteWorker(worker: any) {
    const confirmed = await this.ux.showConfirmation(
      `Are you sure you want to remove worker "${worker.firstname || worker.email}"? They will no longer be able to log in.`,
      'Remove Worker',
    );
    if (!confirmed) return;

    this.ux.showOverlay('Removing worker...');
    try {
      const workerRef = doc(this.afs, 'users-list', worker.id);
      await updateDoc(workerRef, { isdeleted: true });

      // Remove from local list
      this.workers = this.workers.filter(w => w.id !== worker.id);
      this.ux.showSuccess(`Worker "${worker.firstname || worker.email}" has been removed.`);
    } catch (error: any) {
      console.error('Error deleting worker:', error);
      this.ux.showError('Failed to remove worker: ' + error.message);
    } finally {
      this.ux.instantCloseOverlay();
    }
  }
}
